/**
 * API Client - Arquitetura modular e escalável
 * 
 * Estrutura:
 * - HttpClient: Cliente HTTP base com interceptors
 * - Services: Serviços separados por domínio
 * - ApiClient: Classe unificada que agrega todos os services
 */

import { httpClient, HttpClient } from './http-client'
import { authService } from './services/auth.service'
import { profileService } from './services/profile.service'
import { attachmentService } from './services/attachment.service'
import { productService } from './services/product.service'
import { meetingService } from './services/meeting.service'
import { clientAnswersService } from './services/client-answers.service'
import { productToolsService } from './services/product-tools.service'
import { certificateService } from './services/certificate.service'
import { studyService } from './services/study.service'

/**
 * ApiClient - Interface unificada para todos os serviços
 */
class ApiClient extends HttpClient {
  // Services organizados por domínio
  auth = authService
  profile = profileService
  attachments = attachmentService
  products = productService
  meetings = meetingService
  clientAnswers = clientAnswersService
  productTools = productToolsService
  certificates = certificateService
  study = studyService

  // Métodos legacy para backward compatibility
  async login(email: string, password: string, mentorId?: string) {
    return this.auth.login(email, password, mentorId)
  }

  async logout() {
    return this.auth.logout()
  }

  async getProfile(idOrEmail: string, type?: 'complete' | 'simple') {
    if (!idOrEmail || idOrEmail.length === 0) {
      throw new Error('User ID or email is required to fetch profile')
    }
    return this.profile.getProfile(idOrEmail, type)
  }

  async updateProfile(id: string, data: any) {
    return this.profile.updateProfile(id, data)
  }

  async recover(email: string, mentorId?: string) {
    return this.auth.recover(email, mentorId)
  }

  async resetPassword(token: string, password: string) {
    return this.auth.resetPassword(token, password)
  }

  async getClientFiles(clientId: string, params?: any) {
    return this.attachments.getClientFiles(clientId, params)
  }

  async getProductsShowcase(params?: any) {
    return this.products.getProductsShowcase(params)
  }

  async getClientProducts(clientId: string, params?: any) {
    return this.products.getClientProducts(clientId, params)
  }

  async getProductCategories(clientId: string, mentorId: string, options?: { skipCache?: boolean }) {
    return this.products.getProductCategories(clientId, mentorId, options)
  }

  async getProductClasses(productId: string, params?: any) {
    return this.products.getProductClasses(productId, params)
  }

  async getUserMeetings(userId: string, params?: any) {
    return this.meetings.getUserMeetings(userId, params)
  }

  async getSessionStats(profileId: string, params?: any) {
    return this.profile.getSessionStats(profileId, params)
  }

  async getSessionHistory(profileId: string, params?: any) {
    return this.profile.getSessionHistory(profileId, params)
  }

  async updateFirstAccess(data: any) {
    return this.profile.updateFirstAccess(data)
  }

  async enable2FA() {
    return this.auth.enable2FA()
  }

  async verify2FA(token: string) {
    return this.auth.verify2FA(token)
  }

  async globalSearch(params: any) {
    return this.products.globalSearch(params)
  }
}

// Instância singleton
const apiClient = new ApiClient()

export default apiClient

// Exportar services individuais para uso direto
export {
  httpClient,
  authService,
  profileService,
  attachmentService,
  productService,
  meetingService,
  clientAnswersService,
  productToolsService,
  certificateService,
}

// Export types
export type { HttpClient }
